import axiosClient from '../axiosClient';
import type { AxiosRequestConfig } from 'axios';

export interface DatabaseObjectEntity {
  systemObjectId: number;
  databaseObjectId: number;
  tableDefinitionId: number;
  itemName: string;
  dbSchemaName: string;
  dbViewName: string;
  active: boolean;
  icsExternalId: string;
  dbTableName: string;
  dbObjectName: string;
  viewExists: boolean;
}

export async function fetchDatabaseObjectsBySystemObjectId (
  systemObjectId: number,
  signal?: AbortSignal
): Promise<DatabaseObjectEntity[]> {
  const config: AxiosRequestConfig = {
    params: { systemObjectId },
    signal
  };

  const response = await axiosClient.get('v2/database/object', config);
  return response.data;
}

export async function fetchDatabaseObjectsByTableDefinitionId (
  tableDefinitionId: number,
  signal?: AbortSignal
): Promise<DatabaseObjectEntity[]> {
  const config: AxiosRequestConfig = {
    params: { tableDefinitionId },
    signal
  };

  const response = await axiosClient.get('v2/database/object', config);
  return response.data;
}

export async function fetchDatabaseObjectById (
  databaseObjectId: number,
  signal?: AbortSignal
): Promise<DatabaseObjectEntity> {
  const config: AxiosRequestConfig = {
    signal
  };

  const response = await axiosClient.get(`v2/database/object/${databaseObjectId}`, config);
  return response.data;
}

export async function deleteDatabaseObject (
  databaseObjectId: number
): Promise<void> {
  await axiosClient.delete('v2/database/system/view', {
    params: { databaseObjectId }
  });
}

// Update database object
export interface UpdateDatabaseObjectRequest {
  systemObjectId: number
  databaseObjectId: number
  dbSchemaName: string
  dbViewName: string
  itemName: string
  active: boolean
  default: boolean
}

export async function updateDatabaseObject (
  request: UpdateDatabaseObjectRequest
): Promise<void> {
  await axiosClient.put('v2/database/system/object', request);
}

// Set default database object
export async function setDefaultDatabaseObject (
  databaseObjectId: number
): Promise<void> {
  await axiosClient.put('v2/database/object/set-default', null, {
    params: { databaseObjectId }
  });
}

// Toggle database object active
export async function toggleDatabaseObjectActive (
  databaseObjectId: number,
  active: boolean
): Promise<void> {
  await axiosClient.put('v2/database/object/active', null, {
    params: { databaseObjectId, active }
  });
}

// Create view
export interface CreateViewRequest {
  systemObjectId: number
  itemName: string
  dbSchemaName: string
  dbViewName: string
}

export async function createView (
  request: CreateViewRequest
): Promise<void> {
  await axiosClient.post('v2/database/system/create-view', request);
}

// Add existing view
export async function addExistingView (
  request: CreateViewRequest
): Promise<void> {
  await axiosClient.post('v2/database/system/add-existing-view', request);
}

// View definition
export interface ViewDefinitionRequest {
  databaseObjectId: number
  refreshViewOnly: boolean
  sql?: string
}

export async function saveViewDefinition (
  request: ViewDefinitionRequest
): Promise<any> {
  const response = await axiosClient.post('v2/database/system/view-definition', request);
  return response.data;
}

// Fetch views for schema
export async function fetchViewsForSchema (
  searchString: string
): Promise<any[]> {
  const response = await axiosClient.get('v2/database/system/views', {
    params: { searchString }
  });
  return response.data;
}

// Recreate views
export async function recreateViews (
  tableDefinitionId: number
): Promise<void> {
  await axiosClient.post('v2/database/system/recreate-views', null, {
    params: { tableDefinitionId }
  });
}

// Update views safely
export interface ViewUpdateResult {
  success: boolean
  message?: string
  [key: string]: any
}

export async function updateViewsSafely (
  tableDefinitionId: number,
  forceOverride: boolean = false
): Promise<ViewUpdateResult> {
  const response = await axiosClient.post('v2/database/system/update-views-safely', null, {
    params: { tableDefinitionId, forceOverride }
  });
  return response.data;
}

// Check views completeness
export async function checkViewsCompleteness (
  tableDefinitionId: number
): Promise<ViewUpdateResult> {
  const response = await axiosClient.post('v2/database/system/check-views-completeness', null, {
    params: { tableDefinitionId }
  });
  return response.data;
}

// Add missing columns
export async function addMissingColumns (
  tableDefinitionId: number
): Promise<ViewUpdateResult> {
  const response = await axiosClient.post('v2/database/system/add-missing-columns', null, {
    params: { tableDefinitionId }
  });
  return response.data;
}

// Update order
export async function updateDatabaseObjectsOrder (
  databaseObjects: DatabaseObjectEntity[]
): Promise<void> {
  await axiosClient.post('v2/database/system/views', databaseObjects);
}
